import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  IoRocketOutline,
  IoReloadOutline,
  IoDocumentTextOutline,
  IoClose,
  IoCheckmarkCircleOutline,
  IoCalendarOutline,
  IoBusinessOutline,
  IoBarcode,
  IoCheckmarkDoneOutline,
  IoMedkitOutline,        
  IoCubeOutline,          
  IoLocationOutline,    
  IoAlertCircle,    
} from "react-icons/io5";
import toast from "react-hot-toast";
import axiosAuth from "../../../utils/axios";

const ShippingDashboard = () => {
  const [activeTab, setActiveTab] = useState("processing"); // processing | shipped | compression_socks
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showPodModal, setShowPodModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [completedPods, setCompletedPods] = useState({});

  const [podData, setPodData] = useState({
    beneficiary_name: "",
    delivery_address: "",
    supplier_invoice_number: "",
    carrier_delivery_service: "",
    carrier_package_id: "",
    item_description: "",
    quantity_delivered: "",
    date_shipped: "",
    date_delivered: "",
    evidence_carrier_tracking: false,
    evidence_signature_confirmation: false,
  });

  // Lock background scroll when modal is open
  useEffect(() => {
    if (!showPodModal) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showPodModal]);

  const fetchOrders = async (status) => {
    try {
      setLoading(true);
      const axiosInstance = axiosAuth();

      const endpointMap = {
        processing: "orders/processing/",
        shipped: "orders/shipped/",
        compression_socks: "shipping/compressed-socks/",
      };

      const endpoint = endpointMap[status] || endpointMap.processing;
      const response = await axiosInstance.get(endpoint);

      if (response.data.success) {
        setOrders(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders(activeTab);
  }, [activeTab]);

  const openPodModal = (order) => {
    setSelectedOrder(order);

    const isFast = order.order_type === "FAST_ORDER";
    setPodData({
      beneficiary_name: order.patient_name || "",
      delivery_address: [order.street, order.city, order.state, order.zip_code]
        .filter(Boolean)
        .join(", "),
      supplier_invoice_number: "",
      carrier_delivery_service: "",
      carrier_package_id: "",
      item_description: isFast ? order.product_sku || "" : "",
      quantity_delivered: isFast ? order.quantity || "" : "",
      date_shipped: "",
      date_delivered: "",
      evidence_carrier_tracking: false,
      evidence_signature_confirmation: false,
    });

    setShowPodModal(true);
  };

  const handlePodDataChange = (field, value) => {
    setPodData((prev) => ({ ...prev, [field]: value }));
  };

  const isPodFormValid = () => {
    return (
      podData.beneficiary_name &&
      podData.delivery_address &&
      podData.supplier_invoice_number &&
      podData.carrier_delivery_service &&
      podData.carrier_package_id &&
      podData.item_description &&
      podData.quantity_delivered &&
      podData.date_shipped &&
      podData.date_delivered
    );
  };

  const handleSubmitPod = async () => {
    if (!isPodFormValid()) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      setSubmitting(true);
      const axiosInstance = axiosAuth();
      const response = await axiosInstance.post(
        `orders/${selectedOrder.id}/pod/create/`,
        podData
      );

      if (response.data.success) {
        toast.success("✅ Order marked as shipped & POD submitted!");
        setCompletedPods((prev) => ({ ...prev, [selectedOrder.id]: true }));
        setShowPodModal(false);
        setSelectedOrder(null);
        await fetchOrders(activeTab);
      }
    } catch (error) {
      console.error("Error submitting POD:", error);
      toast.error(
        error.response?.data?.error || "Failed to submit Proof of Delivery"
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center px-4">
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-14 h-14 border-4 border-teal-200 border-t-teal-600 dark:border-teal-500/20 dark:border-t-teal-400 rounded-full mx-auto mb-4"
          />
          <p className="text-slate-600 dark:text-slate-300 font-semibold">
            Loading orders…
          </p>
        </div>
      </div>
    );
  }

  const tabCopy = {
    processing: {
      title: "Processing",
      subtitle: "Complete Proof of Delivery and mark orders as shipped.",
      emptyTitle: "All caught up",
      emptyText: "No orders currently in processing status.",
    },
    shipped: {
      title: "Shipped",
      subtitle: "View all shipped orders and their delivery status.",
      emptyTitle: "No shipped orders",
      emptyText: "No orders have been shipped yet.",
    },
    compression_socks: {
      title: "Compression Socks",
      subtitle: "View compression sock orders after biller amount is updated.",
      emptyTitle: "No compression sock orders",
      emptyText: "No biller-ready compression sock orders found.",
    },
  };

  const currentTab = tabCopy[activeTab] || tabCopy.processing;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 -mt-24">
      <div className="pt-24 sm:pt-28 px-4 sm:px-6 lg:px-10 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-teal-700 dark:text-teal-300">
                  Operations
                </p>
                <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100">
                  Shipping Dashboard
                </h1>
                <p className="mt-2 text-sm sm:text-base text-slate-600 dark:text-slate-400">
                  {currentTab.subtitle}
                </p>
              </div>
              <button
                onClick={() => fetchOrders(activeTab)}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:shadow-md dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200"
              >
                <IoReloadOutline className="text-teal-700 dark:text-teal-300" />
                Refresh
              </button>
            </div>

            {/* Tabs */}
            <div className="mt-6 flex gap-2 p-1.5 rounded-2xl bg-slate-100 dark:bg-slate-900/50 w-fit">
              <button
                onClick={() => setActiveTab("processing")}
                className={`relative px-6 py-2.5 rounded-xl text-sm font-extrabold transition-all ${
                  activeTab === "processing"
                    ? "bg-white text-slate-900 shadow-sm dark:bg-slate-800 dark:text-slate-100"
                    : "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
                }`}
              >
                <span className="inline-flex items-center gap-2">
                  <IoRocketOutline className="text-lg" />
                  Processing
                </span>
              </button>
              <button
                onClick={() => setActiveTab("shipped")}
                className={`relative px-6 py-2.5 rounded-xl text-sm font-extrabold transition-all ${
                  activeTab === "shipped"
                    ? "bg-white text-slate-900 shadow-sm dark:bg-slate-800 dark:text-slate-100"
                    : "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
                }`}
              >
                <span className="inline-flex items-center gap-2">
                  <IoCheckmarkDoneOutline className="text-lg" />
                  Shipped
                </span>
              </button>
              <button
                onClick={() => setActiveTab("compression_socks")}
                className={`relative px-6 py-2.5 rounded-xl text-sm font-extrabold transition-all ${
                  activeTab === "compression_socks"
                    ? "bg-white text-slate-900 shadow-sm dark:bg-slate-800 dark:text-slate-100"
                    : "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
                }`}
              >
                <span className="inline-flex items-center gap-2">
                  <IoMedkitOutline className="text-lg" />
                  Compression Socks
                </span>
              </button>
            </div>

            {/* Stats Card */}
            <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900/50">
              <div className="flex items-center justify-between gap-6">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Total {currentTab.title} Orders
                  </p>
                  <p className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100">
                    {orders.length}
                  </p>
                </div>
                <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl bg-teal-50 ring-1 ring-teal-100 grid place-items-center dark:bg-teal-500/10 dark:ring-teal-500/20">
                  {activeTab === "processing" ? (
                    <IoRocketOutline className="text-3xl sm:text-4xl text-teal-700 dark:text-teal-300" />
                  ) : activeTab === "compression_socks" ? (
                    <IoMedkitOutline className="text-3xl sm:text-4xl text-teal-700 dark:text-teal-300" />
                  ) : (
                    <IoCheckmarkDoneOutline className="text-3xl sm:text-4xl text-teal-700 dark:text-teal-300" />
                  )}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Orders List */}
          {orders.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="rounded-3xl border border-slate-200 bg-white p-12 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900/50"
            >
              <div className="mx-auto mb-4 h-16 w-16 rounded-2xl bg-teal-50 ring-1 ring-teal-100 grid place-items-center dark:bg-teal-500/10 dark:ring-teal-500/20">
                {activeTab === "processing" ? (
                  <IoRocketOutline className="text-3xl text-teal-700 dark:text-teal-300" />
                ) : activeTab === "compression_socks" ? (
                  <IoMedkitOutline className="text-3xl text-teal-700 dark:text-teal-300" />
                ) : (
                  <IoCheckmarkDoneOutline className="text-3xl text-teal-700 dark:text-teal-300" />
                )}
              </div>

              <h3 className="text-lg sm:text-xl font-extrabold text-slate-900 dark:text-slate-100">
                {currentTab.emptyTitle}
              </h3>

              <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                {currentTab.emptyText}
              </p>
            </motion.div>
          ) : (
            <div className="space-y-6">
              <AnimatePresence>
                {orders.map((order, index) => {
                  const isFast = order.order_type === "FAST_ORDER";
                  const isCompression = order.order_type === "COMPRESSED_SOCK_ORDER";
                  const podDone = completedPods[order.id];

  return (
    <motion.div
      key={order.id}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -60 }}
      transition={{ delay: index * 0.03 }}
      className="rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900/50"
    >
      <div className="p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span
                className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-extrabold ring-1 ${
                  isFast
                    ? "bg-orange-50 text-orange-700 ring-orange-200 dark:bg-orange-500/10 dark:text-orange-300 dark:ring-orange-500/30"
                    : isCompression
                    ? "bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-500/30"
                    : "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-500/30"
                }`}
              >
                {isFast ? "Fast Order" : isCompression ? "Compression Sock" : "Guided Order"}
              </span>
              <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                Created{" "}
                {new Date(order.created_at).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
              {activeTab === "shipped" && order.shipped_at && (
                <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                  Shipped{" "}
                  {new Date(order.shipped_at).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
              )}
            </div>

            <h3 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100">
              Order #{order.order_number}
            </h3>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <InfoRow label="Provider" value={order.provider_name} />
              <InfoRow
                label="Patient"
                value={order.patient_name || order.patient_name_or_id}
              />
              
              {isCompression ? (
                <>
                  <InfoRow label="Product" value={order.product_sku} />
                  <InfoRow label="Size" value={order.size} />
                  <InfoRow label="HCPC" value={order.hcpc} />
                  <InfoRow label="Compression Status" value={order.compression_sock_status} />
                  <InfoRow label="Medical Records" value={order.medical_records_status} />
                  <InfoRow label="Physician" value={order.physician} />
                  <InfoRow label="Doctor Location" value={order.doctor_location} />
                  <InfoRow label="DOB" value={order.patient_dob} />
                  <InfoRow label="Email" value={order.email_address} />
                  <InfoRow label="Primary Insurance" value={order.primary_insurance} />
                  <InfoRow label="Primary Member ID" value={order.primary_member_id} />
                  <InfoRow label="Date Received" value={order.date_order_received} />
                  <InfoRow label="Date Sent to Billing" value={order.date_sent_to_billing} />
                  <InfoRow label="Collection / Amount" value={order.collection} />
                  <InfoRow label="Biller Comments" value={order.comments} wide />
                </>
              ) : isFast ? (
                <>
                  <InfoRow label="Product SKU" value={order.product_sku} />
                  <InfoRow label="Size" value={order.size} />
                  <InfoRow label="Quantity" value={order.quantity} />
                  <InfoRow label="Treatment Type" value={order.treatment_type} />
                  {order.pack_duration && (
                    <InfoRow label="Pack Duration" value={order.pack_duration} />
                  )}
                </>
              ) : (
                <>
                  <InfoRow
                    label="Care Type"
                    value={order.care_type === "propacks" ? "ProPacks (Conservative)" : "Advanced Care"}
                    icon={<IoMedkitOutline className="inline text-teal-600 dark:text-teal-400" />}
                  />

                  {order.kit_duration && (
                    <InfoRow
                      label="Kit Duration"
                      value={order.kit_duration}
                      icon={<IoCalendarOutline className="inline text-teal-600 dark:text-teal-400" />}
                    />
                  )}

                  {order.care_type === "propacks" && order.propack_config && (
                    <div className="md:col-span-2 rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-950/30">
                      <div className="flex items-start gap-2 mb-2">
                        <IoCubeOutline className="text-lg text-teal-600 dark:text-teal-400 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                            ProPack Kit
                          </p>
                          <div className="mt-2 space-y-2">
                            {order.propack_config.primary && (
                              <div className="text-xs">
                                <span className="font-semibold text-slate-600 dark:text-slate-400">Primary:</span>{" "}
                                <span className="font-bold text-slate-900 dark:text-slate-100">
                                  {order.propack_config.primary.type?.replace(/_/g, " ")?.toUpperCase()}
                                  {order.propack_config.primary.size && ` - ${order.propack_config.primary.size}`}
                                </span>
                              </div>
                            )}

                            {order.propack_config.secondary && (
                              <div className="text-xs">
                                <span className="font-semibold text-slate-600 dark:text-slate-400">Secondary:</span>{" "}
                                <span className="font-bold text-slate-900 dark:text-slate-100">
                                  {order.propack_config.secondary.type?.replace(/_/g, " ")?.toUpperCase()}
                                  {order.propack_config.secondary.size && ` - ${order.propack_config.secondary.size}`}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {order.care_type === "advanced" && (order.graft_name || order.graft_size) && (
                    <>
                      {order.graft_name && (
                        <InfoRow
                          label="Graft Name"
                          value={order.graft_name}
                          icon={<IoMedkitOutline className="inline text-teal-600 dark:text-teal-400" />}
                        />
                      )}
                      {order.graft_size && (
                        <InfoRow
                          label="Graft Size"
                          value={order.graft_size}
                          icon={<IoCubeOutline className="inline text-teal-600 dark:text-teal-400" />}
                        />
                      )}
                    </>
                  )}

                  {order.wound_type && (
                    <InfoRow
                      label="Wound Type"
                      value={order.wound_type}
                      icon={<IoAlertCircle className="inline text-teal-600 dark:text-teal-400" />}
                    />
                  )}

                  {order.wound_location && (
                    <InfoRow
                      label="Wound Location"
                      value={order.wound_location}
                      icon={<IoLocationOutline className="inline text-teal-600 dark:text-teal-400" />}
                    />
                  )}

                  {order.icd10_code && (
                    <InfoRow label="ICD-10 Code" value={order.icd10_code} />
                  )}
                </>
              )}
              
              <InfoRow 
                label="Facility" 
                value={order.facility_name}
                icon={<IoBusinessOutline className="inline text-teal-600 dark:text-teal-400" />}
              />
              <InfoRow
                label="Delivery Address"
                value={`${order.street}, ${order.city}, ${order.state} ${order.zip_code}`}
                wide
              />
              <InfoRow label="Phone" value={order.phone_number} />
              
              {/* Shipping details for shipped orders */}
              {activeTab === "shipped" && (
                <>
                  {order.shipping_via && (
                    <InfoRow 
                      label="Carrier" 
                      value={order.shipping_via}
                      icon={<IoBusinessOutline className="inline text-teal-600 dark:text-teal-400" />}
                    />
                  )}
                  {order.tracking_number && (
                    <InfoRow 
                      label="Tracking Number" 
                      value={order.tracking_number}
                      icon={<IoBarcode className="inline text-teal-600 dark:text-teal-400" />}
                    />
                  )}
                </>
              )}
            </div>
          </div>

          <div className="lg:w-[340px]">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/40">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Status
              </p>
              <p className="mt-1 text-sm font-extrabold text-slate-900 dark:text-slate-100 capitalize">
                {isCompression ? order.compression_sock_status || "Ready for Shipping" : order.status}
              </p>

              {isCompression && (
                <div className="mt-3 rounded-xl px-3 py-2 text-xs font-semibold ring-1 bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-500/30">
                  <div>Amount Updated</div>
                  <div className="mt-1 font-mono text-[11px] break-all">
                    {order.collection || "N/A"}
                  </div>
                </div>
              )}
              {(activeTab === "shipped" || podDone) && (
                <div className="mt-3 rounded-xl px-3 py-2 text-xs font-semibold ring-1 bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/30 flex items-center gap-1">
                  <IoCheckmarkCircleOutline />
                  Shipped
                </div>
              )}
              
              {activeTab === "shipped" && order.tracking_number && (
                <div className="mt-2 rounded-xl px-3 py-2 text-xs font-semibold ring-1 bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-500/10 dark:text-blue-300 dark:ring-blue-500/30">
                  <div className="flex items-center gap-1 mb-1">
                    <IoBusinessOutline />
                    <span>{order.shipping_via}</span>
                  </div>
                  <div className="flex items-center gap-1 font-mono text-[10px]">
                    <IoBarcode />
                    <span>{order.tracking_number}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {activeTab === "processing" && (
          <div className="mt-6">
            <button
              onClick={() => openPodModal(order)}
              disabled={podDone}
              className={`w-full rounded-2xl px-6 py-3.5 text-sm font-extrabold text-white shadow-sm transition hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50 ${
                podDone
                  ? "bg-emerald-600"
                  : "bg-teal-600 hover:bg-teal-700"
              }`}
            >
              {podDone ? (
                <span className="inline-flex items-center justify-center gap-2">
                  <IoCheckmarkCircleOutline className="text-lg" />
                  POD Submitted & Shipped
                </span>
              ) : (
                <span className="inline-flex items-center justify-center gap-2">
                  <IoDocumentTextOutline className="text-lg" />
                  Fill Proof of Delivery & Mark as Shipped
                </span>
              )}
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
})}
              </AnimatePresence>
            </div>
          )}

          {/* POD Modal - Same as before */}
          <AnimatePresence>
            {showPodModal && selectedOrder && (
              <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-24 sm:pt-28 p-4 overflow-y-auto">
                {/* Backdrop */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 z-[9998] bg-black/60 backdrop-blur-sm"
                  onClick={() => !submitting && setShowPodModal(false)}
                />

                {/* Modal */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.98, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.98, y: 20 }}
                  className="relative z-[9999] w-full max-w-4xl max-h-[calc(100vh-7rem)] sm:max-h-[calc(100vh-8rem)] flex flex-col rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
                  role="dialog"
                  aria-modal="true"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-white px-6 py-5 rounded-t-3xl dark:border-slate-700 dark:bg-slate-900 flex-shrink-0">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-wider text-teal-700 dark:text-teal-300">
                        Proof of Delivery
                      </p>
                      <h2 className="mt-1 text-xl sm:text-2xl font-extrabold text-slate-900 dark:text-slate-100">
                        Order #{selectedOrder?.order_number}
                      </h2>
                      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                        Complete required fields to submit POD and mark this order as shipped.
                      </p>
                    </div>

                    <button
                      onClick={() => setShowPodModal(false)}
                      disabled={submitting}
                      className="flex-shrink-0 rounded-full p-2.5 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Close modal"
                    >
                      <IoClose className="text-3xl text-slate-700 dark:text-slate-300" />
                    </button>
                  </div>

                  {/* Body */}
                  <div className="flex-1 overflow-y-auto p-6 space-y-5">
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-950/30">
                      <SectionHeader title="Beneficiary (Patient)" />
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField label="Name *">
                          <input
                            type="text"
                            value={podData.beneficiary_name}
                            onChange={(e) =>
                              handlePodDataChange("beneficiary_name", e.target.value)
                            }
                            className={inputCls}
                            placeholder="Patient full name"
                            disabled={submitting}
                          />
                        </FormField>
                        <FormField label="Delivery Address *">
                          <input
                            type="text"
                            value={podData.delivery_address}
                            onChange={(e) =>
                              handlePodDataChange("delivery_address", e.target.value)
                            }
                            className={inputCls}
                            placeholder="Full delivery address"
                            disabled={submitting}
                          />
                        </FormField>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-950/30">
                      <SectionHeader title="Linking Information (required)" />
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <FormField label="Invoice / Packing Slip # *" icon={<IoDocumentTextOutline />}>
                          <input
                            type="text"
                            value={podData.supplier_invoice_number}
                            onChange={(e) =>
                              handlePodDataChange("supplier_invoice_number", e.target.value)
                            }
                            className={inputCls}
                            placeholder="INV-2025-001"
                            disabled={submitting}
                          />
                        </FormField>

                        <FormField label="Carrier / Delivery Service *" icon={<IoBusinessOutline />}>
                          <select
                            value={podData.carrier_delivery_service}
                            onChange={(e) =>
                              handlePodDataChange("carrier_delivery_service", e.target.value)
                            }
                            className={inputCls}
                            disabled={submitting}
                          >
                            <option value="">Select carrier…</option>
                            <option>FedEx</option>
                            <option>UPS</option>
                            <option>USPS</option>
                            <option>DHL</option>
                            <option>Other</option>
                          </select>
                        </FormField>

                        <FormField label="Package ID / Tracking # *" icon={<IoBarcode />}>
                          <input
                            type="text"
                            value={podData.carrier_package_id}
                            onChange={(e) =>
                              handlePodDataChange("carrier_package_id", e.target.value)
                            }
                            className={inputCls}
                            placeholder="Tracking number"
                            disabled={submitting}
                          />
                        </FormField>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-950/30">
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField label="Item Description *">
                          <input
                            type="text"
                            value={podData.item_description}
                            onChange={(e) =>
                              handlePodDataChange("item_description", e.target.value)
                            }
                            className={inputCls}
                            placeholder="HCPCS, narrative, or brand/model"
                            disabled={submitting}
                          />
                        </FormField>
                        <FormField label="Quantity *">
                          <input
                            type="number"
                            min="1"
                            value={podData.quantity_delivered}
                            onChange={(e) =>
                              handlePodDataChange("quantity_delivered", e.target.value)
                            }
                            className={inputCls}
                            placeholder="e.g. 2"
                            disabled={submitting}
                          />
                        </FormField>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-950/30">
                      <SectionHeader title="Delivery + Shipping Confirmation" />
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField label="Date Shipped / Label Created *" icon={<IoCalendarOutline />}>
                          <input
                            type="date"
                            value={podData.date_shipped}
                            onChange={(e) =>
                              handlePodDataChange("date_shipped", e.target.value)
                            }
                            className={inputCls}
                            disabled={submitting}
                          />
                        </FormField>
                        <FormField label="Expected date of delivery *" icon={<IoCalendarOutline />}>
                          <input
                            type="date"
                            value={podData.date_delivered}
                            onChange={(e) =>
                              handlePodDataChange("date_delivered", e.target.value)
                            }
                            className={inputCls}
                            disabled={submitting}
                          />
                        </FormField>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-950/30">
                      <SectionHeader title="Evidence of Delivery" />
                      <div className="flex flex-col gap-3">
                        {/* <label className="flex items-center gap-3 cursor-pointer text-sm font-semibold text-slate-700 dark:text-slate-300">
                          <input
                            type="checkbox"
                            checked={podData.evidence_carrier_tracking}
                            onChange={(e) =>
                              handlePodDataChange("evidence_carrier_tracking", e.target.checked)
                            }
                            className="w-4 h-4 rounded accent-teal-600"
                            disabled={submitting}
                          />
                          Carrier tracking showing delivered
                        </label> */}
                        <label className="flex items-center gap-3 cursor-pointer text-sm font-semibold text-slate-700 dark:text-slate-300">
                          <input
                            type="checkbox"
                            checked={podData.evidence_signature_confirmation}
                            onChange={(e) =>
                              handlePodDataChange("evidence_signature_confirmation", e.target.checked)
                            }
                            className="w-4 h-4 rounded accent-teal-600"
                            disabled={submitting}
                          />
                          Signature confirmation (if available)
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="border-t border-slate-200 bg-slate-50 px-6 py-4 rounded-b-3xl dark:border-slate-700 dark:bg-slate-900/80 flex-shrink-0">
                    <button
                      onClick={handleSubmitPod}
                      disabled={!isPodFormValid() || submitting}
                      className="w-full rounded-2xl bg-teal-600 px-6 py-4 text-sm font-extrabold text-white shadow-sm transition
                      hover:bg-teal-700 hover:shadow-md
                      focus:outline-none focus:ring-2 focus:ring-teal-500/40
                      disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {submitting ? (
                        <Spinner label="Submitting POD & Marking as Shipped…" />
                      ) : (
                        <span className="inline-flex items-center justify-center gap-2">
                          <IoRocketOutline className="text-lg" />
                          Submit POD & Mark as Shipped
                        </span>
                      )}
                    </button>

                    {!isPodFormValid() && !submitting && (
                      <p className="mt-3 text-center text-xs font-semibold text-amber-700 dark:text-amber-300">
                        Please fill in all required fields (marked with *)
                      </p>
                    )}
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

/* ── Helpers ─────────────────────────────────────────────────────── */

const inputCls =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:ring-2 focus:ring-teal-500/40 disabled:opacity-50 disabled:cursor-not-allowed dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500";

const SectionHeader = ({ title }) => (
  <p className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-3">
    {title}
  </p>
);

const FormField = ({ label, icon, children }) => (
  <div>
    <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">
      {icon ? (
        <span className="inline-flex items-center gap-2">
          <span className="text-teal-700 dark:text-teal-300">{icon}</span>
          {label}
        </span>
      ) : (
        label
      )}
    </label>
    {children}
  </div>
);

const InfoRow = ({ label, value, wide, icon }) => (
  <div className={wide ? "md:col-span-2" : ""}>
    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 flex items-center gap-1">
      {icon}
      {label}
    </p>
    <p className="mt-1 text-sm font-extrabold text-slate-900 dark:text-slate-100 break-all">
      {value || "N/A"}
    </p>
  </div>
);
const Spinner = ({ label }) => (
  <span className="inline-flex items-center justify-center gap-2">
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
      className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
    />
    {label}
  </span>
);

export default ShippingDashboard;